// What Reassign says (spec C6, C7 Toasts and confirm popups). Pure, so it is tested without a browser.
import type {
  Assignee,
  ReassignItem,
  ReassignReason,
  ReassignResult,
  RenewalRow,
} from '../auth/api';

/** The most renewals one bulk Reassign sends. Must match maxItems in apps/server/src/reassign.ts. */
export const maxReassign = 500;

export const reasonLabels: Record<ReassignReason, string> = {
  workload: 'Workload',
  out_of_office: 'Out of office',
  carrier_specialty: 'Carrier specialty',
  other: 'Other',
};

export type Row = Pick<
  RenewalRow,
  'id' | 'insured' | 'carrier' | 'line' | 'policyNumber' | 'handledBy' | 'handoff'
>;

/**
 * What a reassign did, for the toast; `undo` puts every moved renewal back where it was, and
 * `undoHandoffs` cancels the reassigns sent to an AE for approval (their handoff ids).
 */
export type Reassigned = {
  text: string;
  undo: ReassignItem[] | null;
  undoHandoffs?: string[];
  /** The AE's take overs (S15 batch 7): Undo takes each one back. */
  undoTakeOvers?: string[];
};

export const plural = (n: number) => `${n} ${n === 1 ? 'renewal' : 'renewals'}`;

/** The toast after a reassign (spec C7 Toasts: past tense, what happened). */
export function reassignedText(rows: Row[], toName: string | null, result: ReassignResult): string {
  const moved = rows.filter((r) => result.moved.includes(r.id));
  const what = moved.length === 1 ? moved[0].insured : plural(moved.length);
  const head =
    moved.length === 0
      ? 'Nothing was reassigned.'
      : toName
        ? `Reassigned ${what} to ${toName}.`
        : `Returned ${what} to the Unassigned pool.`;
  const n = result.skipped.length;
  if (n === 0) return head;
  return `${head} ${n === 1 ? '1 was' : `${n} were`} left as they were: changed by someone else, closed, or not yours.`;
}

/**
 * The confirm popup's extra line when one renewal has a handoff in play that the reassign ends
 * (spec C6: "Dana Wolfe's pending handoff is cancelled."); empty otherwise.
 */
export function endsHandoffLine(rows: Row[], toId: string | null, meId: string): string {
  const h = rows.length === 1 ? rows[0].handoff : null;
  if (!h || h.to.id === toId) return '';
  const who = h.to.id === meId ? 'Your' : `${h.to.name}'s`;
  return h.state === 'waiting' ? ` ${who} pending handoff is cancelled.` : ` ${who} handoff ends.`;
}

/** The confirm popup for a reassign to an AE, who accepts first (the Renewals board). */
export function aeConfirmCopy(
  rows: Row[],
  ae: { id: string; name: string },
  meId: string,
): { title: string; line: string } {
  const what = rows.length === 1 ? rows[0].insured : plural(rows.length);
  return {
    title: `Reassign ${what} to ${ae.name}?`,
    line: `${rows.length === 1 ? 'They accept' : `${ae.name} accepts`} first. Until then it shows as Awaiting AE.${endsHandoffLine(rows, ae.id, meId)}`,
  };
}

/** The toast after a reassign to an AE (spec C6: "Sent to the AE for approval"). */
export function sentForApprovalText(rows: Row[], aeName: string, sent: string[]): string {
  const done = rows.filter((r) => sent.includes(r.id));
  const what = done.length === 1 ? done[0].insured : plural(done.length);
  const head = done.length === 0 ? 'Nothing was sent.' : `Sent ${what} to ${aeName} for approval.`;
  const n = rows.length - done.length;
  if (n === 0) return head;
  return `${head} ${n === 1 ? '1 was' : `${n} were`} left as they were: changed by someone else, closed, or not yours.`;
}

/** The confirm popup's question and line (spec C6). */
export function confirmCopy(
  rows: Row[],
  to: { id: string; name: string } | null,
  meId: string,
): { title: string; line: string } {
  const what = rows.length === 1 ? rows[0].insured : plural(rows.length);
  if (!to) {
    const from = rows.length === 1 ? rows[0].handledBy?.name : null;
    return {
      title: `Return ${what} to the Unassigned pool?`,
      line:
        (from
          ? `It leaves ${from}'s queue and waits in the Unassigned pool.`
          : 'They wait in the Unassigned pool until someone is picked.') +
        endsHandoffLine(rows, null, meId),
    };
  }
  if (to.id === meId) {
    return {
      title: `Assign ${what} to yourself?`,
      line:
        (rows.length === 1 && rows[0].handledBy
          ? `It leaves ${rows[0].handledBy.name}'s queue and moves to your My renewals.`
          : 'It moves to your My renewals.') + endsHandoffLine(rows, meId, meId),
    };
  }
  const mine = rows.every((r) => r.handledBy?.id === meId);
  return {
    title: `Reassign ${what} to ${to.name}?`,
    line:
      (mine
        ? `It leaves your queue and moves to ${to.name}'s My renewals.`
        : `It moves to ${to.name}'s My renewals.`) + endsHandoffLine(rows, to.id, meId),
  };
}

/**
 * Why the AE may not take a renewal over from Reassign: never from the Manager or the Admin (the
 * server agrees). From a Renewal VA, another AE or the pool it is Take over (S15 batch 7).
 */
export function heldBy(rows: Row[], people: Assignee[], meId: string): string | null {
  // The Manager and the Admin are not in the list of people work goes to.
  const held = rows.find(
    (r) => r.handledBy && r.handledBy.id !== meId && !people.some((p) => p.id === r.handledBy!.id),
  )?.handledBy;
  return held ? `Held by ${held.name}. Ask them to hand it over.` : null;
}
